import type { WidgetComponent } from "@viz/presentation";

// Minimal Markdown renderer: paragraphs, headings, bold, italic, code, links.
// Real apps would import a library (`marked`/`react-markdown`); we use a small
// inline implementation to keep this package zero-deps for the v1 footprint.

function renderInline(text: string): React.ReactNode {
  const out: React.ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
  let last = 0; let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[2]) out.push(<strong key={out.length}>{m[2]}</strong>);
    else if (m[3]) out.push(<em key={out.length}>{m[3]}</em>);
    else if (m[4]) out.push(<code key={out.length} className="rounded bg-slate-100 px-1 py-px font-mono text-xs text-slate-800">{m[4]}</code>);
    else if (m[5] && m[6]) out.push(<a key={out.length} href={m[6]} className="text-sky-700 underline">{m[5]}</a>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const Markdown: WidgetComponent = ({ props }) => {
  const md = String(props.content ?? "");
  const lines = md.split(/\r?\n/);
  const blocks: React.ReactNode[] = [];
  let para: string[] = [];
  const flush = () => { if (para.length) { blocks.push(<p key={blocks.length} className="mb-3">{renderInline(para.join(" "))}</p>); para = []; } };
  for (const line of lines) {
    if (/^#{1,6}\s/.test(line)) {
      flush();
      const m = /^(#{1,6})\s(.+)$/.exec(line)!;
      const level = m[1]!.length;
      const cls = `mb-2 mt-4 font-semibold text-slate-900 text-${level === 1 ? "lg" : "base"}`;
      const inner = renderInline(m[2]!);
      const k = String(blocks.length);
      blocks.push(
        level === 1 ? <h1 key={k} className={cls}>{inner}</h1>
        : level === 2 ? <h2 key={k} className={cls}>{inner}</h2>
        : level === 3 ? <h3 key={k} className={cls}>{inner}</h3>
        : level === 4 ? <h4 key={k} className={cls}>{inner}</h4>
        : level === 5 ? <h5 key={k} className={cls}>{inner}</h5>
        :               <h6 key={k} className={cls}>{inner}</h6>,
      );
    } else if (line.trim() === "") flush();
    else para.push(line);
  }
  flush();
  return <div className="prose max-w-none px-4 py-3 text-sm leading-relaxed text-slate-800">{blocks}</div>;
};

export default Markdown;
